import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { DEPOSIT_STATUSES, type DepositStatus } from '@accounting/shared';
import { useDeposits, useSetDepositCleared, useVoidDeposit } from '../../hooks/useDeposits';
import { useAccounts } from '../../hooks/useAccounts';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { formatAccountingDate, formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/deposits/')({
  component: DepositsPage,
});

function statusTag(s: DepositStatus) {
  if (s === 'cleared') return <span className="tag ok">cleared</span>;
  if (s === 'voided') return <span className="tag bad">voided</span>;
  return <span className="tag warn">outstanding</span>;
}

function DepositsPage() {
  const companyId = useActiveCompanyId();
  const [status, setStatus] = useState<DepositStatus | ''>('');
  const { data: deposits = [], isLoading, error } = useDeposits(companyId, {
    status: status || undefined,
  });
  const { data: accounts = [] } = useAccounts(companyId);
  const acctName = new Map(accounts.map((a) => [a.id, `${a.code} — ${a.name}`]));
  const clear = useSetDepositCleared(companyId);
  const voidDeposit = useVoidDeposit(companyId);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Deposits</h1>
        <p className="muted">No active company.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
          Go to Companies
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 24, margin: 0 }}>Deposits</h1>
          <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>Deposit register</p>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <label style={{ fontSize: 13 }}>
            Status{' '}
            <select value={status} onChange={(e) => setStatus(e.target.value as DepositStatus | '')}>
              <option value="">All</option>
              {DEPOSIT_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
          <Link to="/deposits/new" className="btn-primary" style={{ textDecoration: 'none' }}>
            + Record a deposit
          </Link>
        </div>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load deposits.</p>}

      {!isLoading && deposits.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>No deposits yet.</p>
          <Link to="/deposits/new" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
            Record your first deposit
          </Link>
        </div>
      )}

      {deposits.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Received from</th>
              <th>Deposited to</th>
              <th>Reference</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
              <th>Status</th>
              <th style={{ width: 1 }}></th>
            </tr>
          </thead>
          <tbody>
            {deposits.map((d) => (
              <tr key={d.id} style={{ opacity: d.status === 'voided' ? 0.6 : 1 }}>
                <td>{formatAccountingDate(d.depositDate)}</td>
                <td>{d.payerName}</td>
                <td className="muted">{acctName.get(d.bankAccountId) ?? '—'}</td>
                <td className="muted">{d.reference ?? '—'}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(d.amount)}</td>
                <td>{statusTag(d.status)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <Link to="/deposits/$id" params={{ id: d.id }} className="btn-secondary" style={{ textDecoration: 'none', marginRight: 6 }}>
                    View
                  </Link>
                  {d.status !== 'voided' && (
                    <>
                      <button
                        className="btn-secondary"
                        style={{ marginRight: 6 }}
                        disabled={clear.isPending}
                        onClick={() => clear.mutate({ id: d.id, cleared: d.status !== 'cleared' })}
                      >
                        {d.status === 'cleared' ? 'Un-clear' : 'Mark cleared'}
                      </button>
                      <button
                        className="btn-danger"
                        disabled={voidDeposit.isPending}
                        onClick={() => {
                          const reason = prompt('Void this deposit? Reason:');
                          if (reason && reason.trim()) voidDeposit.mutate({ id: d.id, reason: reason.trim() });
                        }}
                      >
                        Void
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
